import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Format phone number as (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const phoneNumber = value.replace(/\D/g, '');
  const match = phoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) return value;
  return !match[2] ? match[1] 
    : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
};

/**
 * Clean phone number to just digits
 */
const cleanPhoneNumber = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Validate phone number
 */
const validatePhoneNumber = (value) => {
  const cleaned = cleanPhoneNumber(value);
  // Only check for exactly 10 digits, ignore formatting
  return cleaned.length === 10;
};

/**
 * Welcome Component
 * 🏠 Main landing page with combined login and registration
 */
const Welcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, error, isAuthenticated } = useAuthContext();
  const [activeView, setActiveView] = useState('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Handle login form changes
  const handleLoginChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setLoginData({
      ...loginData,
      [e.target.name]: value,
    });
  };

  // Handle register form changes
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      const formattedNumber = formatPhoneNumber(value);
      const isValid = validatePhoneNumber(formattedNumber);
      setRegisterData({
        ...registerData,
        [name]: formattedNumber,
      });
      if (value && !isValid) {
        setPhoneError('Please enter a valid 10-digit phone number');
      } else {
        setPhoneError('');
      }
    } else if (name === 'password' || name === 'confirmPassword') {
      setRegisterData({
        ...registerData,
        [name]: value,
      });
      setPasswordError('');
    } else {
      setRegisterData({
        ...registerData,
        [name]: value,
      });
    }
  };

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      await login(loginData);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle register form submission
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate passwords match
    if (registerData.password !== registerData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (registerData.password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(registerData.phoneNumber)) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      setIsSubmitting(true);
      const { confirmPassword, ...registrationData } = registerData;
      
      // Clean up phone number before sending to server
      const cleanedData = {
        ...registrationData,
        phoneNumber: cleanPhoneNumber(registrationData.phoneNumber),
        // Ensure date is in ISO format for the server
        dateOfBirth: new Date(registrationData.dateOfBirth).toISOString(),
      };
      
      await register(cleanedData);
      
      // Success notification
      toast.success('Account created! Please verify your email.');
      
      // After successful registration, redirect to email verification immediately
      navigate('/verify-email', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle between login and register views
  const toggleView = (view) => {
    setActiveView(view);
  };

  // Render the form content based on active view
  const renderFormContent = () => {
    if (activeView === 'login') {
      return (
        <div className="login-form" key="login">
          <form onSubmit={handleLoginSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-gray-700 font-medium text-sm" htmlFor="login-email">
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleLoginChange}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm shadow-sm"
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-gray-700 font-medium text-sm" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                name="password"
                value={loginData.password}
                onChange={handleLoginChange}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm shadow-sm"
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={loginData.rememberMe}
                  onChange={handleLoginChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isSubmitting}
                />
                <span className="ml-2 text-gray-700 text-sm">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 rounded-xl text-white font-medium text-sm transition duration-200 ease-in-out disabled:opacity-50 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      );
    }

    // For register, redirect to invitation verification
    return (
      <div className="register-form" key="register">
        <div className="text-center py-8">
          <h3 className="text-xl font-semibold mb-4">Create a New Account</h3>
          <p className="text-gray-600 mb-6">
            To register, you'll need a valid invitation code. Please verify your invitation first.
          </p>
          <button
            onClick={() => navigate('/verify-invitation')}
            className="w-full py-3 px-4 rounded-xl text-white font-medium text-sm transition duration-200 ease-in-out bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl"
          >
            Verify Invitation
          </button>
          <div className="mt-4">
            <Link 
              to="/request-invitation" 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Don't have an invitation? Request one
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#6C5DD3] to-[#8247E5] relative">
      {/* White overlay for faded effect */}
      <div className="absolute inset-0 bg-white opacity-25"></div>
      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl p-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
          {/* Left section - App intro */}
          <div className="w-full md:w-2/5 bg-gradient-to-br from-blue-500 to-purple-600 p-8 text-white flex flex-col justify-center relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative z-10">
              <h1 className="text-3xl font-bold mb-4">MnyMny</h1>
              <p className="text-lg mb-8 font-light">Take control of your finances with intelligent AI insights.</p>
              
              <div className="space-y-5">
                <div className="flex items-start">
                  <div className="bg-blue-400 bg-opacity-30 p-2 rounded-lg mr-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5z"></path>
                      <path d="M11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Track Finances Together</h3>
                    <p className="text-sm text-blue-50">Share and manage financial data individually or with partners</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-400 bg-opacity-30 p-2 rounded-lg mr-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
                      <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Budget Collaboratively and with AI</h3>
                    <p className="text-sm text-blue-50">Create smart budgets with AI-powered insights</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-400 bg-opacity-30 p-2 rounded-lg mr-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Connect Your Accounts</h3>
                    <p className="text-sm text-blue-50">Link bank accounts for automatic tracking all in one place</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-400 bg-opacity-30 p-2 rounded-lg mr-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">AI-Powered Financial Insights</h3>
                    <p className="text-sm text-blue-50">Get personalized recommendations for debt payoff, savings, and retirement</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 text-sm border-t border-blue-400 border-opacity-30 pt-4">
                <p>
                  <Link to="/about" className="text-blue-50 hover:text-white underline underline-offset-2">About</Link>
                  {" • "}
                  <Link to="/mission" className="text-blue-50 hover:text-white underline underline-offset-2">Our Mission</Link>
                  {" • "}
                  <span className="opacity-75">Coming Soon: </span>
                  <Link to="/docs" className="text-blue-50 hover:text-white underline underline-offset-2">Docs</Link>
                  {" • "}
                  <Link to="/forum" className="text-blue-50 hover:text-white underline underline-offset-2">Forum</Link>
                  {" • "}
                  <Link to="/privacy" className="text-blue-50 hover:text-white underline underline-offset-2">Privacy Policy</Link>
                  {" • "}
                  <Link to="/data-retention" className="text-blue-50 hover:text-white underline underline-offset-2">Data Retention</Link>
                </p>
              </div>
            </div>
          </div>

          {/* Right section - Auth forms */}
          <div className="w-full md:w-3/5 p-8 md:p-12">
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 p-1 rounded-xl shadow-md">
                <button
                  onClick={() => toggleView('login')}
                  className={`px-8 py-2 rounded-lg transition-all ${
                    activeView === 'login'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium shadow-md'
                      : 'bg-transparent text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => toggleView('register')}
                  className={`px-8 py-2 rounded-lg transition-all ${
                    activeView === 'register'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium shadow-md'
                      : 'bg-transparent text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Sign Up
                </button>
              </div>
            </div>

            {(error || passwordError || phoneError) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm shadow-md">
                <div className="flex">
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {error || passwordError || phoneError}
                </div>
              </div>
            )}

            {renderFormContent()}
            
            <p className="mt-8 text-center text-gray-600 text-sm">
              {activeView === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button 
                    onClick={() => toggleView('register')} 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Create one now
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button 
                    onClick={() => toggleView('login')} 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome; 